/* eslint-disable no-undef */
class CartRemoveButton extends HTMLElement {
  constructor () {
    super()
    this.addEventListener('click', (event) => {
      event.preventDefault()
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items')
      cartItems.updateQuantity(this.dataset.index, 0)
    })
  }
}

customElements.define('cart-remove-button', CartRemoveButton)

class CartItems extends HTMLElement {
  constructor () {
    super()

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus')

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0)

    // Deleting/decrementing two different lines in quick succession fires
    // overlapping /cart/change.js requests; responses can land out of
    // order, and the last one to ARRIVE would otherwise overwrite the DOM
    // even if it's the older, less-mutated snapshot. Stamping each request
    // with a token and only rendering the response for the latest-ISSUED
    // request (see updateQuantity) makes the last-dispatched request win
    // regardless of network arrival order.
    this.requestToken = 0

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event)
    }, 300)

    this.addEventListener('change', this.debouncedOnChange.bind(this))
  }

  stripLocalePrefix (path) {
    if (!path) return '/'
    const locales = ['fr-fr', 'de-de', 'it-it']
    for (const loc of locales) {
      if (path.toLowerCase().startsWith(`/${loc}/`) || path.toLowerCase() === `/${loc}`) {
        return path.slice(loc.length + 1) || '/'
      }
    }
    return path
  }

  onChange (event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.itemKey)
  }

  getSectionsToRender () {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ]
  }

  /**
   * `itemKey` (the stable `variantId:propertieshash` line key, when the
   * caller has one) takes priority over `line` for the actual request.
   * Rapid clicks across multiple lines fire overlapping /cart/change.js
   * requests; by the time a later one reaches the server, an earlier
   * removal has already shifted every subsequent line's position, so a
   * positional `line` can silently target the wrong item (e.g. leaving an
   * orphaned free-gift line behind). `line` is still used for the
   * loading-overlay lookup below, which only needs the DOM as it looked
   * at click time.
   */
  updateQuantity (line, quantity, name, itemKey) {
    this.enableLoading(line)
    const requestId = ++this.requestToken

    const body = JSON.stringify({
      ...(itemKey ? { id: itemKey } : { line }),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      /* eslint-disable-next-line camelcase */
      sections_url: window.location.pathname,
    })

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text()
      })
      .then(async (state) => {
        // A newer request has been issued since this one — its response
        // (whenever it arrives) is authoritative, so drop this one rather
        // than let an out-of-order reply overwrite a fresher render.
        if (requestId !== this.requestToken) return

        const parsedState = JSON.parse(state)

        if (!parsedState.sections || parsedState.sections === null) {
          try {
            const sectionsToFetch = this.getSectionsToRender().map((section) => section.section)
            const fetchPromises = sectionsToFetch.map((sectionId) =>
              fetch(`${window.location.pathname}?section_id=${sectionId}`).then((res) =>
                res.ok ? res.text().then((text) => ({ id: sectionId, html: text })) : null,
              ),
            )
            const results = await Promise.all(fetchPromises)
            const sections = {}
            results.forEach((result) => {
              if (result) sections[result.id] = result.html
            })
            if (Object.keys(sections).length) {
              parsedState.sections = sections
            }
          } catch (e) {}
        }

        // Re-check: a newer request may have been dispatched while the
        // fallback section fetch above was in flight.
        if (requestId !== this.requestToken) return

        this.classList.toggle('is-empty', parsedState.item_count === 0)
        const cartDrawerWrapper = document.querySelector('cart-drawer')
        const cartFooter = document.getElementById('main-cart-footer')

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0)
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0)

        if (parsedState.sections) {
          this.getSectionsToRender().forEach(section => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id)
            if (parsedState.sections[section.section]) {
              elementToReplace.innerHTML =
                this.getSectionInnerHTML(parsedState.sections[section.section], section.selector)
            }
          })
        }

        window.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState }))

        this.updateLiveRegions(line, parsedState.item_count)
        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`)
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus()
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        this.disableLoading()
      }).catch(() => {
        if (requestId !== this.requestToken) return
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'))
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors')
        errors.textContent = window.cartStrings.error
        this.disableLoading()
      })
  }

  updateLiveRegions (line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`)
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`)
      lineItemError
        .querySelector('.cart-item__error-text')
        .innerHTML = window.cartStrings.quantityError.replace(
          '[quantity]',
          quantityElement.value,
        )
    }

    this.currentItemCount = itemCount
    this.lineItemStatusElement.setAttribute('aria-hidden', true)

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText')
    cartStatus.setAttribute('aria-hidden', false)

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true)
    }, 1000)
  }

  getSectionInnerHTML (html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML
  }

  enableLoading (line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    mainCartItems.classList.add('cart__items--disabled')

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`)
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'))

    document.activeElement.blur()
    this.lineItemStatusElement.setAttribute('aria-hidden', false)
  }

  disableLoading () {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    mainCartItems.classList.remove('cart__items--disabled')
  }
}

customElements.define('cart-items', CartItems)

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor () {
      super()

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value })
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      }, 300))
    }
  })
}

// Enhanced CartDrawerItems class with loading support for new grid items
class CartDrawerItems extends CartItems {
  getSectionsToRender () {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ]
  }

  enableLoading (line) {
    // Call parent method for existing functionality
    super.enableLoading(line)

    // Enable loading for new grid-based cart items
    const newCartItems = this.querySelectorAll('.cart-drawer__cart-item')
    if (newCartItems.length > 0 && line <= newCartItems.length) {
      const cartItem = newCartItems[line - 1]
      if (cartItem) {
        cartItem.classList.add('cart-drawer__cart-item--loading')
        cartItem.setAttribute('data-loading', 'true')

        // Enable loading on quantity selector
        const quantitySelector = cartItem.querySelector('quantity-selector')
        if (quantitySelector) {
          quantitySelector.enableLoading()
        }

        // Enable loading on remove button
        const removeButton = cartItem.querySelector('cart-drawer-remove-button')
        if (removeButton) {
          removeButton.enableLoading()
        }
      }
    }
  }

  disableLoading () {
    // Call parent method for existing functionality
    super.disableLoading()

    // Disable loading for new grid-based cart items
    const newCartItems = this.querySelectorAll('.cart-drawer__cart-item--loading, .cart-drawer__cart-item[data-loading="true"]')
    newCartItems.forEach(cartItem => {
      cartItem.classList.remove('cart-drawer__cart-item--loading')
      cartItem.removeAttribute('data-loading')

      // Disable loading on quantity selector
      const quantitySelector = cartItem.querySelector('quantity-selector')
      if (quantitySelector) {
        quantitySelector.disableLoading()
      }

      // Disable loading on remove button
      const removeButton = cartItem.querySelector('cart-drawer-remove-button')
      if (removeButton) {
        removeButton.disableLoading()
      }
    })
  }

  getSectionInnerHTML (html, selector) {
    const innerHTML = super.getSectionInnerHTML(html, selector)

    setTimeout(() => {
      this.initializeCustomElements()
      if (window.tentboxInitCartDrawerFaders) window.tentboxInitCartDrawerFaders(document)
    }, 0)

    return innerHTML
  }

  initializeCustomElements () {
    // Custom elements with connectedCallback will reinitialize automatically
    // when they're re-added to the DOM, so we don't need to do anything special here
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)

function getCartDrawerFaderRoot (container) {
  if (container.matches('#CartDrawer-Scroll, .cart-drawer__scroll')) {
    return container.closest('.cart-drawer__scroll-shell') || container
  }
  return container
}

function initCartDrawerFaders (root = document) {
  const containers = root.querySelectorAll('#CartDrawer-Scroll, .cart-drawer__empty[data-scroll-container="true"]')
  containers.forEach((container) => {
    const faderRoot = getCartDrawerFaderRoot(container)
    const topFader = faderRoot.querySelector('.cart-drawer__fader--top')
    const bottomFader = faderRoot.querySelector('.cart-drawer__fader--bottom')
    if (!topFader && !bottomFader) return

    if (container._faderObserver) {
      try { container._faderObserver.disconnect() } catch (e) {}
      container._faderObserver = null
    }

    const isScrollable = container.scrollHeight > container.clientHeight
    if (!isScrollable) {
      if (topFader) topFader.classList.remove('show')
      if (bottomFader) bottomFader.classList.remove('show')
    } else {
      if (topFader) topFader.classList.add('show')
      if (bottomFader) bottomFader.classList.add('show')
    }

    const topSentinel = container.querySelector('.cart-drawer__sentinel--top')
    const bottomSentinel = container.querySelector('.cart-drawer__sentinel--bottom')
    if (topSentinel && bottomSentinel && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === topSentinel) {
            if (entry.isIntersecting) {
              if (topFader) topFader.classList.remove('show')
            } else {
              if (topFader) topFader.classList.add('show')
            }
          } else if (entry.target === bottomSentinel) {
            if (entry.isIntersecting) {
              if (bottomFader) bottomFader.classList.remove('show')
            } else {
              if (bottomFader) bottomFader.classList.add('show')
            }
          }
        }
      }, { root: container, threshold: 0.01 })

      container._faderObserver = observer
      observer.observe(topSentinel)
      observer.observe(bottomSentinel)
      container.dataset.fadersInit = 'true'
      return
    }

    const onScroll = () => {
      const maxScroll = container.scrollHeight - container.clientHeight
      const showTop = container.scrollTop > 0
      const showBottom = container.scrollTop < (maxScroll - 1)
      if (topFader) topFader.classList.toggle('show', showTop)
      if (bottomFader) bottomFader.classList.toggle('show', showBottom)
    }

    container.addEventListener('scroll', onScroll, { passive: true })
    container.dataset.fadersInit = 'true'
    setTimeout(onScroll, 0)
  })
}

window.tentboxInitCartDrawerFaders = initCartDrawerFaders

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawerFaders(document)
})

// QuantitySelector custom element (shared between drawer and main cart if used)
if (!customElements.get('quantity-selector')) {
  class QuantitySelector extends HTMLElement {
    connectedCallback () {
      this.button = this.querySelector('[data-quantity-button]')
      this.dropdown = this.querySelector('.cart-drawer__quantity-dropdown')
      this.currentSpan = this.querySelector('.cart-drawer__quantity-current')
      this.decreaseBtn = this.querySelector('[data-quantity-decrease]')
      this.increaseBtn = this.querySelector('[data-quantity-increase]')
      this.minusIcon = this.querySelector('.cart-drawer__quantity-stepper-icon--minus')
      this.binIcon = this.querySelector('.cart-drawer__quantity-stepper-icon--bin')
      this.itemKey = this.dataset.itemKey
      this.lineIndex = parseInt(this.dataset.lineIndex, 10)
      this.quantityOffset = parseInt(this.dataset.quantityOffset, 10) || 0
      this.maxQuantity = parseInt(this.dataset.quantityMax, 10) || 20
      this.productTitle = this.dataset.productTitle || 'item'

      if (this.decreaseBtn && this.increaseBtn) {
        this.mode = 'stepper'
        this.initStepper()
        this.syncStepperUi(this.getDisplayQuantity())
      } else if (this.button && this.dropdown) {
        this.mode = 'dropdown'
        this.init()
      }
    }

    init () {
      this.button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.toggleDropdown()
      })

      this.dropdown.addEventListener('click', (e) => {
        const quantityButton = e.target.closest('[data-quantity-value]')
        if (quantityButton) {
          e.preventDefault()
          e.stopPropagation()
          const newQuantity = parseInt(quantityButton.dataset.quantityValue, 10)
          this.applyPayload(newQuantity)
          this.closeDropdown()
        }
      })

      const list = this.dropdown.querySelector('.cart-drawer__quantity-list')
      const fader = this.dropdown.querySelector('.cart-drawer__quantity-fader')
      const updateFader = () => {
        if (!list || !fader) return
        const isScrollable = list.scrollHeight > list.clientHeight
        const atBottom = Math.ceil(list.scrollTop + list.clientHeight) >= list.scrollHeight
        fader.style.opacity = (!isScrollable || atBottom) ? '0' : '1'
      }
      if (list) {
        list.addEventListener('scroll', updateFader)
        setTimeout(updateFader, 0)
      }

      document.addEventListener('click', (e) => {
        if (!e.target.closest(`[data-item-key="${this.itemKey}"] .cart-drawer__cart-item-quantity`)) {
          this.closeDropdown()
        }
      })
    }

    initStepper () {
      this.decreaseBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const current = this.getDisplayQuantity()
        if (current <= 1) {
          this.applyPayload(0)
        } else {
          this.applyPayload(current - 1)
        }
      })
      this.increaseBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const current = this.getDisplayQuantity()
        if (current >= this.maxQuantity) return
        this.applyPayload(current + 1)
      })
    }

    getDisplayQuantity () {
      if (!this.currentSpan) return 1
      const n = parseInt(String(this.currentSpan.textContent).trim(), 10)
      return Number.isFinite(n) ? n : 1
    }

    applyPayload (selectedQuantity) {
      const container = this.closest('cart-drawer-items, cart-items') || document.querySelector('cart-drawer-items') || document.querySelector('cart-items')
      if (!container || !Number.isFinite(this.lineIndex) || this.lineIndex <= 0) return
      this.enableLoading()
      const lineQuantity = selectedQuantity === 0 ? 0 : selectedQuantity + this.quantityOffset
      container.updateQuantity(this.lineIndex, lineQuantity, undefined, this.itemKey)
    }

    toggleDropdown () {
      const isOpen = !this.dropdown.classList.contains('hidden')
      document.querySelectorAll('.cart-drawer__quantity-dropdown').forEach((d) => d.classList.add('hidden'))
      document.querySelectorAll('[data-quantity-button]').forEach((b) => b.setAttribute('aria-expanded', 'false'))
      if (!isOpen) {
        if (!this.isButtonVisible()) return
        this.dropdown.classList.remove('hidden')
        this.dropdown.style.visibility = 'hidden'
        this.positionDropdown()
        this.dropdown.style.visibility = ''
        this.button.setAttribute('aria-expanded', 'true')
      }
    }

    closeDropdown () {
      this.dropdown.classList.add('hidden')
      this.button.setAttribute('aria-expanded', 'false')
    }

    getScrollContainer () {
      return this.closest('[data-scroll-container="true"]') || document
    }

    isButtonVisible () {
      const container = this.getScrollContainer()
      const buttonRect = this.button.getBoundingClientRect()

      if (container === document || container === document.documentElement) {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth
        const fullyVisibleVertically = buttonRect.top >= 0 && buttonRect.bottom <= viewportHeight
        const horizontallyVisible = buttonRect.right > 0 && buttonRect.left < viewportWidth
        return fullyVisibleVertically && horizontallyVisible
      }

      const containerRect = container.getBoundingClientRect()
      const fullyVisibleVertically = buttonRect.top >= containerRect.top && buttonRect.bottom <= containerRect.bottom
      const horizontallyVisible = buttonRect.right > containerRect.left && buttonRect.left < containerRect.right
      return fullyVisibleVertically && horizontallyVisible
    }

    positionDropdown () {
      const container = this.getScrollContainer()
      const buttonRect = this.button.getBoundingClientRect()

      this.dropdown.classList.remove('cart-drawer__quantity-dropdown--top')
      this.dropdown.classList.remove('mb-1')
      this.dropdown.classList.add('mt-1')

      const dropdownHeight = this.dropdown.getBoundingClientRect().height || 0
      let spaceBelow
      let spaceAbove

      if (container === document || container === document.documentElement) {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        spaceBelow = viewportHeight - buttonRect.bottom
        spaceAbove = buttonRect.top
      } else {
        const containerRect = container.getBoundingClientRect()
        spaceBelow = containerRect.bottom - buttonRect.bottom
        spaceAbove = buttonRect.top - containerRect.top
      }

      const shouldFlipToTop = spaceBelow < dropdownHeight + 8 && spaceAbove > spaceBelow
      if (shouldFlipToTop) {
        this.dropdown.classList.add('cart-drawer__quantity-dropdown--top')
        this.dropdown.classList.remove('mt-1')
        this.dropdown.classList.add('mb-1')
      }
    }

    enableLoading () {
      const cartItem = this.closest('.cart-drawer__cart-item')
      if (cartItem) {
        cartItem.classList.add('cart-drawer__cart-item--loading')
        cartItem.setAttribute('data-loading', 'true')
      }
      if (this.decreaseBtn) this.decreaseBtn.disabled = true
      if (this.increaseBtn) this.increaseBtn.disabled = true
      if (this.button) this.button.disabled = true
    }

    disableLoading () {
      const cartItem = this.closest('.cart-drawer__cart-item')
      if (cartItem) {
        cartItem.classList.remove('cart-drawer__cart-item--loading')
        cartItem.removeAttribute('data-loading')
      }
      if (this.button) this.button.disabled = false
      if (this.decreaseBtn) this.decreaseBtn.disabled = false
      if (this.increaseBtn) this.increaseBtn.disabled = false
      if (this.mode === 'stepper') {
        this.syncStepperUi(this.getDisplayQuantity())
      }
    }

    syncStepperUi (quantity) {
      const atMin = quantity <= 1
      if (this.minusIcon && this.binIcon) {
        this.minusIcon.classList.toggle('hidden', atMin)
        this.binIcon.classList.toggle('hidden', !atMin)
      }
      if (this.decreaseBtn) {
        const removeLabel = `Remove ${this.productTitle} from cart`
        const decLabel = `Decrease quantity for ${this.productTitle}`
        this.decreaseBtn.setAttribute('aria-label', atMin ? removeLabel : decLabel)
      }
      if (this.increaseBtn) {
        this.increaseBtn.disabled = quantity >= this.maxQuantity
      }
    }

    updateCurrentValue (quantity) {
      if (this.currentSpan) this.currentSpan.textContent = quantity
      this.querySelectorAll('[data-quantity-value]').forEach((option) => {
        const value = parseInt(option.dataset.quantityValue, 10)
        if (value === quantity) {
          option.classList.add('bg-oat', 'font-medium')
          option.setAttribute('aria-selected', 'true')
        } else {
          option.classList.remove('bg-oat', 'font-medium')
          option.setAttribute('aria-selected', 'false')
        }
      })
      if (this.mode === 'stepper') {
        this.syncStepperUi(quantity)
      }
    }
  }
  customElements.define('quantity-selector', QuantitySelector)
}

// Remove button custom element (shared)
if (!customElements.get('cart-drawer-remove-button')) {
  class CartDrawerRemoveButton extends HTMLElement {
    // no-op constructor

    connectedCallback () {
      this.button = this.querySelector('[data-remove-item]')
      this.itemKey = this.dataset.itemKey
      this.lineIndex = parseInt(this.dataset.lineIndex)
      if (this.button) this.init()
    }

    init () {
      this.button.addEventListener('click', (e) => {
        e.preventDefault()
        const container = this.closest('cart-drawer-items, cart-items') || document.querySelector('cart-drawer-items') || document.querySelector('cart-items')
        if (container && this.lineIndex > 0) {
          this.enableLoading()
          const cartItem = this.closest('.cart-drawer__cart-item')
          if (cartItem) cartItem.classList.add('cart-drawer__cart-item--loading')
          container.updateQuantity(this.lineIndex, 0, undefined, this.itemKey)
        }
      })
    }

    enableLoading () {
      if (this.button) this.button.disabled = true
    }

    disableLoading () {
      if (this.button) this.button.disabled = false
    }
  }
  customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton)
}

// Cart Cross-sell Button with Variant Selection
if (!customElements.get('cart-cross-sell-button')) {
  class CartCrossSellButton extends HTMLElement {
    // no-op constructor

    connectedCallback () {
      this.button = this.querySelector('button')
      this.productId = this.dataset.productId
      this.productHandle = this.dataset.productHandle
      this.productTitle = this.dataset.productTitle
      this.hasMultipleVariants = this.dataset.hasMultipleVariants === 'true'
      this.variantIds = this.dataset.variantIds ? this.dataset.variantIds.split(',') : []
      this.optionsName = this.dataset.optionsName
      this.cardId = this.dataset.cardId

      // Create custom modal if we have multiple variants
      if (this.hasMultipleVariants) {
        this.createCustomModal()
      }

      if (this.button) this.init()
    }

    createCustomModal () {
      // Add required CSS styles for the modal
      if (!document.getElementById('cart-cross-sell-modal-styles')) {
        const style = document.createElement('style')
        style.id = 'cart-cross-sell-modal-styles'
        style.textContent = `
          .cart-cross-sell-modal[data-open='false'] {
            visibility: hidden;
            opacity: 0;
            pointer-events: none;
            transition: visibility 0s linear 0.4s, opacity 0.2s;
          }

          .cart-cross-sell-modal[data-open='true'] {
            visibility: visible;
            opacity: 1;
            pointer-events: auto;
            transition: visibility 0s linear 0s, opacity 0.2s;
          }

          @media only screen and (max-width: 900px) {
            .cart-cross-sell-modal .cart-cross-sell-modal__content {
              transform: translateY(100%);
              transition: transform 0.2s 0s ease;
            }

            .cart-cross-sell-modal[data-open='true'] .cart-cross-sell-modal__content {
              transform: translateY(0);
              transition: transform 0.2s 0.1s ease;
            }
          }

          .cart-cross-sell-modal svg {
            width: 100%;
            height: 100%;
          }

          .cart-cross-sell-button__spinner {
            display: none;
          }

          cart-cross-sell-button[loading] .cart-cross-sell-button__spinner {
            display: flex;
          }

          cart-cross-sell-button[loading] .cart-cross-sell-button__text {
            opacity: 0;
          }
        `
        document.head.appendChild(style)
      }

      // Create modal HTML structure matching reusable modal exactly
      this.modal = document.createElement('div')
      this.modal.className = 'cart-cross-sell-modal relative z-[99999999999] md:z-[99999999999]'
      this.modal.setAttribute('data-open', 'false')

      this.modal.innerHTML = `
        <div class="cart-cross-sell-modal__container fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div class="cart-cross-sell-modal__content overscroll-contain bg-white rounded-lg max-h-[80vh] min-h-[200px] max-w-[800px] min-w-[300px] w-full md:w-auto overflow-hidden flex flex-col p-4 md:p-4 fixed md:relative bottom-0 md:bottom-auto rounded-b-none md:rounded-b-lg">
            <div class="cart-cross-sell-modal__header flex items-center justify-between gap-4 w-full mb-4 border-b border-clay pb-2 select-none">
              <div class="cart-cross-sell-modal__title inline-flex items-center text-lg font-body font-semibold min-h-8 min-w-0 flex-1">
                ${window.cartStrings.variantModalTitle.replace('[option_name]', this.optionsName)}
              </div>
              <button type="button" class="cart-cross-sell-modal__close shrink-0 text-xl leading-none w-8 h-8 p-1 flex items-center justify-center bg-white rounded-full" aria-label="${window.cartStrings.variantModalCloseLabel}">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div class="cart-cross-sell-modal__body-wrapper overflow-y-auto flex-grow relative">
              <div class="cart-cross-sell-modal__body">
                <div class="cart-cross-sell-modal__options"></div>
              </div>
            </div>
          </div>
        </div>
      `

      // Add modal to body
      document.body.appendChild(this.modal)

      // Add event listeners
      // Overlay click (click on container but not content)
      const container = this.modal.querySelector('.cart-cross-sell-modal__container')
      container.addEventListener('click', (e) => {
        if (e.target === container) {
          this.hideModal()
        }
      })

      this.modal.querySelector('.cart-cross-sell-modal__close').addEventListener('click', () => {
        this.hideModal()
      })

      // ESC key to close
      this.handleEscapeKey = (e) => {
        if (e.key === 'Escape' && this.modal.getAttribute('data-open') === 'true') {
          this.hideModal()
        }
      }
      document.addEventListener('keydown', this.handleEscapeKey)
    }

    init () {
      this.button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (this.hasMultipleVariants) {
          this.handleMultipleVariants()
        } else {
          // Single variant - add directly to cart
          this.addSingleVariantToCart()
        }
      })
    }

    async handleMultipleVariants () {
      this.enableLoading()

      try {
        const embeddedData = this.getEmbeddedVariantData()
        const variantData = embeddedData
          ? embeddedData.variants
          : await this.fetchVariantData(this.variantIds)

        // After client-side model filtering only a single variant may remain
        // (e.g. one model with one size) - add it straight to the cart.
        if (embeddedData && variantData.length === 1) {
          await this.addVariantToCart(variantData[0].id)
          return
        }

        this.openVariantModal(variantData, embeddedData?.optionsName)
      } catch (error) {
        console.error('Error fetching variant data:', error)
        this.showError(window.cartStrings.variantModalLoadError)
      } finally {
        this.disableLoading()
      }
    }

    // Mirrors the model normalisation in snippets/cart-cross-sell-card.liquid
    normaliseModel (value) {
      if (value === null || value === undefined) return ''
      let out = String(value).replace('TentBox ', '')
      out = out.trim().toLowerCase()
      return out
    }

    getPdpModel () {
      const root = this.closest('[data-cross-sells-source]')
      return root && root.dataset.pdpModel ? root.dataset.pdpModel : ''
    }

    getEmbeddedVariantData () {
      const card = this.closest('[data-cart-cross-sell-card]')
      if (!card) return null

      const script = card.querySelector('script[data-cross-sell-variant-data]')
      if (!script) return null

      let parsed
      try {
        parsed = JSON.parse(script.textContent)
      } catch (error) {
        return null
      }
      if (!parsed?.variants?.length) return null

      const options = Array.isArray(parsed.options) ? parsed.options : []
      const modelOptionName = parsed.modelOptionName || 'TentBox Model'
      const normalisedModel = this.normaliseModel(this.getPdpModel())

      // Locate the model option and the option we want the customer to pick
      // (e.g. Regular / Tall) so we can filter the embedded variants down to the
      // PDP model the customer is viewing.
      let modelOptionPosition = null
      let labelOptionPosition = null
      if (normalisedModel !== '') {
        const isModelOptionName = (name) => {
          if (name === modelOptionName || name === 'Model') return true
          const down = String(name).toLowerCase()
          return down.includes('modèle') || down.includes('modell') || down.includes('model')
        }
        let modelOption = options.find(o => isModelOptionName(o.name))
        if (!modelOption) {
          modelOption = options.find((o) => {
            const values = [o.values].flat().filter(Boolean)
            return values.some((value) => this.normaliseModel(value) === normalisedModel)
          })
        }
        if (modelOption) {
          modelOptionPosition = modelOption.position
          const labelOption = options.find(o => o.position !== modelOptionPosition)
          if (labelOption) labelOptionPosition = labelOption.position
        }
      }

      const valueAtPosition = (variant, position) => {
        if (position === 1) return variant.option1
        if (position === 2) return variant.option2
        if (position === 3) return variant.option3
        return null
      }

      let matched = parsed.variants
      let optionsName = parsed.optionsName
      let filterApplied = false
      if (modelOptionPosition) {
        const filtered = parsed.variants.filter(
          v => this.normaliseModel(valueAtPosition(v, modelOptionPosition)) === normalisedModel,
        )
        if (filtered.length > 0) {
          matched = filtered
          filterApplied = true
          const labelOption = options.find(o => o.position === labelOptionPosition)
          if (labelOption) optionsName = labelOption.name
        } else {
          // Product has a Model option but no variants match the current PDP
          // model. Mirror the Liquid fallback in cart-cross-sell-card.liquid:
          // collapse to the first variant so handleMultipleVariants adds it
          // straight to the cart instead of opening a confusing modal full of
          // variants for other models.
          matched = parsed.variants.slice(0, 1)
        }
      }

      return {
        optionsName: optionsName || this.optionsName,
        variants: matched.map(variant => {
          let label = variant.title
          if (filterApplied && labelOptionPosition) {
            const labelValue = valueAtPosition(variant, labelOptionPosition)
            if (labelValue) label = labelValue
          }
          return {
            id: String(variant.id),
            title: label,
            price: Number(variant.price) / 100,
            compareAtPrice: variant.compareAtPrice !== null && variant.compareAtPrice !== undefined
              ? Number(variant.compareAtPrice) / 100
              : null,
            available: variant.available !== false,
          }
        }),
      }
    }

    async fetchVariantData (variantIds) {
      const variantData = []
      let successfulFetches = 0

      // First, try the variant endpoint approach
      for (const id of variantIds) {
        try {
          const response = await fetch(`/variants/${id}.json`)

          if (!response.ok) {
            continue
          }

          const data = await response.json()
          variantData.push({
            title: data.product_variant.title,
            price: data.product_variant.price,
            compareAtPrice: data.product_variant.compare_at_price ?? null,
            id: String(data.product_variant.id),
            available: data.product_variant.available,
          })
          successfulFetches++
        } catch (error) {
          console.error(`[Cart Cross-sell] Error fetching variant ${id}:`, error)
        }
      }

      // If individual variant fetching failed, try fallback method using product data
      if (successfulFetches === 0) {
        const productEndpoints = []
        if (this.productHandle) productEndpoints.push(`/products/${this.productHandle}.js`)
        if (this.productId) productEndpoints.push(`/products/${this.productId}.js`)

        for (const endpoint of productEndpoints) {
          try {
            const response = await fetch(endpoint)

            if (response.ok) {
              const productData = await response.json()

              // Extract variant data from product
              productData.variants?.forEach(variant => {
                if (variantIds.includes(String(variant.id)) && variant.available) {
                  variantData.push({
                    title: variant.title,
                    price: variant.price,
                    compareAtPrice: variant.compare_at_price ?? null,
                    id: String(variant.id),
                    available: variant.available,
                  })
                }
              })

              // If we got data, break out of the loop
              if (variantData.length > 0) break
            }
          } catch (error) {
            console.error(`[Cart Cross-sell] Fallback method failed for ${endpoint}:`, error)
          }
        }
      }

      return variantData.filter(variant => variant.available !== false)
    }

    openVariantModal (variantData, optionsNameOverride) {
      if (!this.modal) {
        console.error('[Cart Cross-sell] Custom modal not found')
        return
      }

      const modalOptionsElement = this.modal.querySelector('.cart-cross-sell-modal__options')
      const optionsName = optionsNameOverride || this.optionsName

      // The header title is built once at creation with the unfiltered options
      // name; refresh it so it reflects the picker option (e.g. "Car height").
      const titleEl = this.modal.querySelector('.cart-cross-sell-modal__title')
      if (titleEl && window.cartStrings?.variantModalTitle) {
        titleEl.textContent = window.cartStrings.variantModalTitle.replace('[option_name]', optionsName)
      }

      // Check if we have variant data
      if (!variantData || variantData.length === 0) {
        console.error('[Cart Cross-sell] No variant data available')
        modalOptionsElement.innerHTML = `<div class="text-center p-4 text-iron">${window.cartStrings.variantModalNoVariants}</div>`
        this.showModal()
        return
      }

      // Create variant selection buttons
      let variantButtons = ''
      variantData.forEach((variant) => {
        const priceNum = parseFloat(variant.price)
        const capRaw = variant.compareAtPrice
        const capNum =
          capRaw !== null && capRaw !== undefined && capRaw !== ''
            ? parseFloat(capRaw)
            : NaN
        const showCompare =
          Number.isFinite(priceNum) &&
          Number.isFinite(capNum) &&
          capNum > priceNum

        const formattedPrice = this.formatPrice(variant.price)
        const compareHtml = showCompare
          ? `<span class="cart-cross-sell-modal__variant-price-compare price-display-wrapper__compare line-through text-sm font-normal text-alizarin pointer-events-none">${this.formatPrice(variant.compareAtPrice)}</span>`
          : ''

        variantButtons += `
          <button 
            type="button" 
            class="cart-cross-sell-modal__variant-button flex rounded overflow-hidden border border-clay items-center justify-between gap-2 mb-2 py-2 px-3 hover:bg-oat transition-colors"
            data-variant-id="${variant.id}"
            data-variant-title="${variant.title}"
            data-variant-price="${variant.price}"
          >
            <span class="cart-cross-sell-modal__variant-title text-sm font-body flex-grow text-left font-normal pointer-events-none">
              ${variant.title}
            </span>
            <span class="cart-cross-sell-modal__variant-price text-sm pointer-events-none">
              <span class="cart-cross-sell-modal__variant-price-inner price-display-wrapper__row-inner flex flex-wrap items-center justify-end gap-1.5 min-w-0">
                <span class="cart-cross-sell-modal__variant-price-current price-display-wrapper__current font-medium text-black pointer-events-none">${formattedPrice}</span>
                ${compareHtml}
              </span>
            </span>
          </button>
        `
      })

      const modalContent = `
        <div class="cart-cross-sell-modal__content-wrapper flex flex-col gap-4">
          <div class="cart-cross-sell-modal__description text-sm text-iron">
            ${window.cartStrings.variantModalDescription.replace('[option_name]', window.cartStrings.downcaseOptionNames !== false ? optionsName.toLowerCase() : optionsName)}
          </div>
          <div class="cart-cross-sell-modal__variants-list grid grid-cols-1 w-full gap-2">
            ${variantButtons}
          </div>
        </div>
      `

      modalOptionsElement.innerHTML = modalContent

      // Add event listeners for variant buttons (remove any existing listeners first)
      const existingHandler = this.variantClickHandler
      if (existingHandler) {
        modalOptionsElement.removeEventListener('click', existingHandler)
      }

      this.variantClickHandler = (e) => {
        const variantButton = e.target.closest('.cart-cross-sell-modal__variant-button[data-variant-id]')
        if (variantButton) {
          const variantId = variantButton.dataset.variantId
          this.addVariantToCart(variantId)
          this.hideModal()
        }
      }

      modalOptionsElement.addEventListener('click', this.variantClickHandler)

      // Show modal
      this.showModal()
    }

    showModal () {
      if (this.modal) {
        this.modal.setAttribute('data-open', 'true')

        // Prevent body scroll but don't interfere with cart drawer scroll management
        if (!document.body.style.overflow) {
          document.body.style.overflow = 'hidden'
        }
      }
    }

    hideModal () {
      if (this.modal) {
        this.modal.setAttribute('data-open', 'false')

        // Only restore body scroll if we set it (don't interfere with cart drawer)
        if (document.body.style.overflow === 'hidden') {
          document.body.style.overflow = ''
        }
      }
    }

    async addVariantToCart (variantId) {
      this.enableLoading()

      const cartDrawer = document.querySelector('cart-drawer')

      try {
        // Mirror the no-variant product-form flow: request the cart sections in the
        // add call, then hand them to the drawer's renderContents so it re-renders
        // and opens, with the same loading/spinner behaviour.
        const sectionIds = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map(section => section.id)
          : ['cart-drawer', 'cart-icon-bubble']

        const cartAddPayload = {
          id: variantId,
          quantity: 1,
          sections: sectionIds,
        }
        // eslint-disable-next-line camelcase -- Shopify Cart API field name
        cartAddPayload.sections_url = window.location.pathname

        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartAddPayload),
        })

        const parsed = await response.json()
        if (!response.ok || parsed.status) {
          throw new Error(parsed.description || window.cartStrings.variantModalAddError)
        }

        let finalResponse = parsed
        if (!parsed.sections || parsed.sections === null) {
          try {
            const [drawerRes, bubbleRes] = await Promise.all([
              fetch(`${window.location.pathname}?section_id=cart-drawer`),
              fetch(`${window.location.pathname}?section_id=cart-icon-bubble`),
            ])
            const sections = {}
            if (drawerRes.ok) sections['cart-drawer'] = await drawerRes.text()
            if (bubbleRes.ok) sections['cart-icon-bubble'] = await bubbleRes.text()
            if (Object.keys(sections).length) finalResponse = { ...parsed, sections }
          } catch (e) {}
        }

        try {
          if (localStorage.getItem('fg_debug') === '1' || new URLSearchParams(window.location.search).has('fg_debug')) {
            /* eslint-disable camelcase */
            console.log('[free-gift] cart-cross-sell addVariantToCart', {
              variantId,
              item_count: finalResponse.item_count,
              items: (finalResponse.items || []).map((item) => ({
                title: item.product_title || item.title,
                variant_id: item.variant_id,
                qty: item.quantity,
                final_price: item.final_price,
                _free_gift: item.properties?._free_gift,
                _promo: item.properties?._promo,
              })),
              hasSections: Boolean(finalResponse.sections),
            })
            /* eslint-enable camelcase */
          }
        } catch (_e) {}

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty')
          cartDrawer.renderContents(finalResponse)
        }

        window.dispatchEvent(new CustomEvent('cart:updated', { detail: finalResponse }))

        this.showSuccess(window.cartStrings.variantModalAddSuccess)
      } catch (error) {
        console.error('Error adding to cart:', error)
        this.showError(window.cartStrings.variantModalAddError)
      } finally {
        this.disableLoading()
      }
    }

    async addSingleVariantToCart () {
      // For single variant products, use the first (and only) variant
      if (this.variantIds.length > 0) {
        await this.addVariantToCart(this.variantIds[0])
      }
    }

    closeModal () {
      this.hideModal()
    }

    // Cleanup when component is removed
    disconnectedCallback () {
      if (this.modal) {
        this.modal.remove()
      }
      if (this.handleEscapeKey) {
        document.removeEventListener('keydown', this.handleEscapeKey)
      }
    }

    formatPrice (price) {
      if (window.FinanceUtils?.formatMoneyAmount) {
        return window.FinanceUtils.formatMoneyAmount(price)
      }
      const priceNum = parseFloat(price)
      return `£${priceNum.toFixed(2)}`
    }

    showSuccess (message) {
      console.log(`[Cart Cross-sell] Success: ${message}`)
      // Could be enhanced with toast notifications
    }

    showError (message) {
      console.error(`[Cart Cross-sell] Error: ${message}`)
      // Could be enhanced with toast notifications
    }

    enableLoading () {
      this.setAttribute('loading', 'true')
      if (this.button) {
        this.button.disabled = true
        this.button.setAttribute('aria-busy', 'true')
      }
    }

    disableLoading () {
      this.removeAttribute('loading')
      if (this.button) {
        this.button.disabled = false
        this.button.setAttribute('aria-busy', 'false')
      }
    }
  }

  customElements.define('cart-cross-sell-button', CartCrossSellButton)
}
